// Moved from `src/hooks/mutation/useMutateUserOnboardingSubTask.ts` (now the app
// delegate) with the identity D2 slice (ARCFE-1062). Unblocked by D1
// (ARCFE-1037), which carved `updateUserPendingOnboardingSubtasks` into
// `../api/user`. The filename keeps its original basename (no `Update`), as
// every moved file in this program does.
//
// Severed per ARCFE-921: the destructive toast. `invalidateCurrentUser()`
// crosses as a port for the OT4 reason — it is COMPOSITE (GET_USER *and* the
// commerce GET_PRODUCTS behind one try/catch).
//
// THE `PendingSubStep` TYPE DID NOT MOVE, on purpose. The app's hand-rolled one
// references `Page` from `@/hooks/useTutorial`, an app module this package
// cannot import; it stays exported from the delegate so its (zero external)
// importers are untouched. This hook takes the CONTRACTS `PendingSubStep` — the
// schema's own component, which D1 already established is structurally identical
// — so callers pass their existing values unchanged.
import { logger } from '@heidi/runtime/logger';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { GET_USER, type PendingSubStep, type UserResponse } from '../../contracts';
import { updateUserPendingOnboardingSubtasks } from '../api/user';
import { useQueryUser } from '../query/useQueryUser';

const UPDATE_USER_ONBOARDING_SUB_TASK = 'useMutateUpdateUserOnboardingSubTask';

export interface UseMutateUpdateUserOnboardingSubTaskOptions {
  /** App-reaction port: fired where `invalidateCurrentUser()` fired. */
  onOnboardingSubTaskUpdated?: () => void;
  /**
   * App-reaction port: fired where the legacy destructive toast fired, after
   * the log. Takes the raw `Error` because that toast's description is
   * `error.message`.
   */
  onOnboardingSubTaskError?: (error: Error) => void;
}

/** PATCH /user/onboarding-subtasks */
export function useMutateUpdateUserOnboardingSubTask(
  options?: UseMutateUpdateUserOnboardingSubTaskOptions
) {
  const { data: userData } = useQueryUser();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [UPDATE_USER_ONBOARDING_SUB_TASK],
    mutationFn: async (subTask: PendingSubStep) => {
      if (subTask && userData) {
        await updateUserPendingOnboardingSubtasks(subTask);
      }
    },
    onSuccess: () => {
      options?.onOnboardingSubTaskUpdated?.();
    },
    onMutate: (subTask: PendingSubStep) => {
      queryClient.setQueryData([GET_USER], (oldData: UserResponse | undefined) => {
        if (!oldData) return oldData;

        return {
          ...oldData,
          current_onboarding_sub_task: subTask,
        };
      });
    },
    onError: (error) => {
      logger.error('Error:', error);
      options?.onOnboardingSubTaskError?.(error);
    },
  });
}
